import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    getHours,
    setHours,
    getMinutes,
    setMinutes,
    isSameDay,
    isSameMonth,
    isBefore,
    startOfDay
} from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
    return twMerge(clsx(inputs));
}

const DateTimePicker = ({ onCancel, onSet, initialDate }) => {
    const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [isAm, setIsAm] = useState(getHours(selectedDate) < 12);

    const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
    const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

    const onDateClick = (day) => setSelectedDate(day);

    const changeHour = (increment) => {
        let hours = getHours(selectedDate) + increment;
        // Simple wrapping logic
        if (hours > 23) hours = 0;
        if (hours < 0) hours = 23;

        setSelectedDate(setHours(selectedDate, hours));
        setIsAm(hours < 12);
    };

    const changeMinute = (increment) => {
        let newMinutes = getMinutes(selectedDate) + increment;
        // Round to nearest 5 or 1 for smoother UX, simply adding here
        if (newMinutes > 59) newMinutes = 0;
        if (newMinutes < 0) newMinutes = 59;
        setSelectedDate(setMinutes(selectedDate, newMinutes));
    };

    const toggleAmPm = () => {
        const currentHours = getHours(selectedDate);
        if (isAm) {
            // Switch to PM: Add 12 if currently < 12
            setSelectedDate(setHours(selectedDate, currentHours + 12));
        } else {
            // Switch to AM: Subtract 12
            setSelectedDate(setHours(selectedDate, currentHours - 12));
        }
        setIsAm(!isAm);
    };

    const quickOptions = [
        { label: 'Today', action: () => setSelectedDate(new Date()) },
        { label: 'Tomorrow', action: () => setSelectedDate(addDays(new Date(), 1)) },
        { label: 'Next Week', action: () => setSelectedDate(addDays(new Date(), 7)) },
    ];

    // --- Grid Generation ---
    const renderHeader = () => {
        return (
            <div className="flex justify-between items-center mb-4 px-2 text-slate-100">
                <button onClick={handlePrevMonth} className="p-1 hover:bg-slate-800 rounded">
                    <ChevronLeft size={20} />
                </button>
                <div className="font-bold text-lg">
                    {format(currentMonth, 'MMMM yyyy')}
                </div>
                <button onClick={handleNextMonth} className="p-1 hover:bg-slate-800 rounded">
                    <ChevronRight size={20} />
                </button>
            </div>
        );
    };

    const renderDays = () => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return (
            <div className="grid grid-cols-7 mb-2">
                {days.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-slate-400">
                        {day}
                    </div>
                ))}
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = '';

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, 'd');
                const cloneDay = day;
                const isSelected = isSameDay(day, selectedDate);
                const isCurrentMonth = isSameMonth(day, monthStart);
                const isPast = isBefore(day, startOfDay(new Date()));

                days.push(
                    <div
                        key={day}
                        className={cn(
                            "p-2 text-center text-sm cursor-pointer relative flex items-center justify-center h-10 w-10 mx-auto rounded-full transition-all",
                            !isCurrentMonth && "text-slate-600",
                            isCurrentMonth && "text-slate-300",
                            isPast && "text-slate-700 cursor-not-allowed", // Dim past dates
                            isSelected && "bg-teal-500 text-white font-bold shadow-lg shadow-teal-500/30",
                            !isSelected && !isPast && "hover:bg-slate-800"
                        )}
                        onClick={() => !isPast && onDateClick(cloneDay)}
                    >
                        {formattedDate}
                    </div>
                );
                day = addDays(day, 1);
            }
            rows.push(
                <div className="grid grid-cols-7" key={day}>
                    {days}
                </div>
            );
            days = [];
        }
        return <div>{rows}</div>;
    };

    const renderTimePicker = () => {
        const displayHours = format(selectedDate, 'hh');
        const displayMinutes = format(selectedDate, 'mm');

        return (
            <div className="flex items-center justify-center gap-2 mt-6 mb-4">
                {/* Hour */}
                <div className="flex flex-col items-center">
                    <button onClick={() => changeHour(1)} className="text-slate-400 hover:text-white"><ChevronUp size={20} /></button>
                    <div className="bg-white text-slate-900 font-bold text-xl w-12 h-10 rounded flex items-center justify-center">
                        {displayHours}
                    </div>
                    <button onClick={() => changeHour(-1)} className="text-slate-400 hover:text-white"><ChevronDown size={20} /></button>
                </div>

                <span className="text-2xl font-bold text-slate-400 pb-1">:</span>

                {/* Minute */}
                <div className="flex flex-col items-center">
                    <button onClick={() => changeMinute(1)} className="text-slate-400 hover:text-white"><ChevronUp size={20} /></button>
                    <div className="bg-white text-slate-900 font-bold text-xl w-12 h-10 rounded flex items-center justify-center">
                        {displayMinutes}
                    </div>
                    <button onClick={() => changeMinute(-1)} className="text-slate-400 hover:text-white"><ChevronDown size={20} /></button>
                </div>

                {/* AM/PM */}
                <div className="flex flex-col items-center ml-2">
                    <div
                        className="border border-slate-600 rounded px-2 py-2 cursor-pointer hover:bg-slate-800 transition-colors"
                        onClick={toggleAmPm}
                    >
                        <span className="text-slate-200 font-bold text-sm tracking-widest">
                            {isAm ? 'AM' : 'PM'}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col md:flex-row gap-4">

            {/* Quick Options Sidebar (To satisfy requirement) */}
            <div className="flex flex-row md:flex-col gap-2 p-2 bg-slate-900/50 rounded-xl h-fit">
                {quickOptions.map((opt) => (
                    <button
                        key={opt.label}
                        onClick={opt.action}
                        className="text-xs text-slate-400 hover:text-teal-400 border border-slate-700 hover:border-teal-500 rounded px-3 py-1 transition-all whitespace-nowrap"
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Main Widget */}
            <div className="bg-[#0f1016] p-6 rounded-3xl shadow-2xl w-[340px] border border-slate-800">
                {renderHeader()}
                {renderDays()}
                {renderCells()}

                <div className="my-4 border-t border-slate-800"></div>

                {renderTimePicker()}

                <div className="flex justify-between items-center mt-6 px-4">
                    <button
                        onClick={onCancel}
                        className="text-slate-400 hover:text-white font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSet(selectedDate)}
                        className="text-white hover:text-teal-400 font-bold transition-colors"
                    >
                        Set
                    </button>
                </div>
            </div>
        </div>
    );
};

DateTimePicker.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSet: PropTypes.func.isRequired,
    initialDate: PropTypes.instanceOf(Date)
};

export default DateTimePicker;